import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './kpiElement.css';

export function MetricHoverBorder() {
  return (
    <svg className="metric-border-svg" aria-hidden="true" focusable="false">
      <rect className="metric-border-rect" pathLength="100" />
    </svg>
  );
}

const KpiElement = ({ metricValue, metricTitle, icon, children }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const metricRef = useRef(null);

  useEffect(() => {
    const targetValue = parseFloat(metricValue);
    const duration = 200;
    const frameRate = 60;
    const totalFrames = (duration / 1000) * frameRate;
    const step = (targetValue - displayValue) / totalFrames;

    let frame = 0;

    const counter = setInterval(() => {
      setDisplayValue((prevValue) => {
        frame += 1;
        const nextValue = prevValue + step;

        if (frame >= totalFrames) {
          clearInterval(counter);
          return targetValue;
        }

        return nextValue;
      });
    }, 1000 / frameRate);

    return () => clearInterval(counter);
  }, [metricValue, displayValue]);

  useLayoutEffect(() => {
    if (isHovered && metricRef.current && children) {
      const rect = metricRef.current.getBoundingClientRect();
      const tooltipWidth = 300;
      const padding = 12;
      const viewportWidth = window.innerWidth;

      let left = rect.left + rect.width / 2;

      if (left - tooltipWidth / 2 < padding) {
        left = padding + tooltipWidth / 2;
      }
      if (left + tooltipWidth / 2 > viewportWidth - padding) {
        left = viewportWidth - padding - tooltipWidth / 2;
      }

      setTooltipPos({
        top: rect.bottom + 10,
        left: left,
      });
    }
  }, [isHovered, children]);

  return (
    <div
      className="metric"
      ref={metricRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MetricHoverBorder />
      <div className="metric-content">
        <p className="metric-header">
          {icon && <FontAwesomeIcon icon={icon} className="metric-icon" />}
          {metricTitle}
        </p>
        <p className="metric-value">{displayValue.toFixed(1)}</p>
      </div>
      {isHovered && children && createPortal(
        <div
          className="kpi-usage-tooltip"
          style={{ top: `${tooltipPos.top}px`, left: `${tooltipPos.left}px` }}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

export default KpiElement;
